import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useGetScatterPlotData } from '@restDataAccess';
import { MappingCollections, MeasurementScatterPlotRequest } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import Highcharts from '@/components/chart/highcharts';
import { useGetInventoriesSensors } from '@/dataAccess/rest';
import { useEventClassUnitPair } from '@/hooks';
import { roundToTwoDecimals, toApiDateTime } from '@/utils';

import { useSiteContext } from '../../../../providers';
import { AutoResizeHighChart } from '../../AutoResizeHighChart.tsx';
import { useChartOptionsWithLocale, useChartTimeRange } from '../../hooks';
import { ICardDataState, ScatterPlotChartConfigType } from '../../types';
import {
  defaultChartOptions,
  defaultLuxonChartDateTimeFormat,
  defaultTooltipOptions,
  defaultXAxisOptions,
  defaultYAxisOptions,
  getLocaleFromLanguage,
  scatterPlotColor,
  scatterPlotMarkerRadius,
  textColor,
  updateChartLocale,
} from '../defaultHighChartOptions.ts';

const options = {
  ...defaultChartOptions,
  chart: {
    ...defaultChartOptions.chart,
    type: 'scatter',
    spacingTop: 10,
    spacingBottom: 22,
  },
};

type ScatterPlotHighChartProps = {
  config: ScatterPlotChartConfigType;
  setChartDataStat: (props: ICardDataState) => void;
  onChartNameChange?: (chartName: string | undefined) => void;
};

export const ScatterPlotHighChart = ({
  config,
  setChartDataStat,
  onChartNameChange,
}: ScatterPlotHighChartProps) => {
  const providerNames = MappingCollections.classNameToFriendlyProviderName;
  const highChartsRef = useRef<HighchartsReact.RefObject>(null);
  const { units } = useEventClassUnitPair();
  const { currentSite: { id: siteInventoryId } = {} } = useSiteContext();

  const { t, i18n } = useTranslation(['chartConfig']);

  const { sensorIdA, providerA, providerB, sensorIdB } = config;

  const { xUnit, yUnit } = useMemo(
    () => ({
      xUnit: units[providerB],
      yUnit: units[providerA],
    }),
    [units, providerA, providerB]
  );

  const { start, end, updateInterval } = useChartTimeRange(config.type, config.interval, {
    start: config.start ?? DateTime.now(),
    end: config.end ?? DateTime.now(),
  });

  //**** Getting historical data ****//
  const query = useMemo<MeasurementScatterPlotRequest>(() => {
    return {
      start: toApiDateTime(start ?? DateTime.now()),
      end: toApiDateTime(end ?? DateTime.now()),
      inventoryIdA: config.sensorIdA,
      inventoryIdB: config.sensorIdB,
      providerA: config.providerA,
      providerB: config.providerB,
    };
  }, [config, end, start]);

  const { data: sensors } = useGetInventoriesSensors(siteInventoryId);

  const getSensorName = useCallback(
    (sensorId: string) => sensors?.find(s => s.id === sensorId)?.name ?? sensorId,
    [sensors]
  );

  const { data, isLoading, isError, isFetched, isRateLimited } = useGetScatterPlotData(
    query,
    updateInterval.toMillis()
  );

  /**
   * Generate axis labels
   * Remarks: Ignore the eslint warning here, to keep clean code
   */
  // eslint-disable-next-line react-x/no-unnecessary-use-callback
  const getAxisLabel = useCallback(
    (sensorId: string, provider: string) => {
      const sensorName = getSensorName(sensorId);
      const providerName = providerNames[provider];
      return `${sensorName}: ${providerName}`;
    },
    [getSensorName, providerNames]
  );

  useEffect(() => {
    setChartDataStat({
      isLoading: isLoading,
      isError: isError,
      isEmpty: isFetched && !data,
      isRateLimited: isRateLimited,
    });
  }, [data, isError, isFetched, isLoading, isRateLimited, setChartDataStat]);

  // Update chart name in parent component
  useEffect(() => {
    const correlationString = data?.correlation
      ? t('chartConfig:scatterPlot.correlation', { value: roundToTwoDecimals(data.correlation) })
      : '';
    const covarianceString = data?.covariance
      ? t('chartConfig:scatterPlot.covariance', { value: roundToTwoDecimals(data.covariance) })
      : '';
    const chartName =
      !correlationString && !covarianceString
        ? undefined
        : [correlationString, covarianceString].filter(Boolean).join(' • ');
    onChartNameChange?.(chartName);
  }, [data?.correlation, data?.covariance, t, onChartNameChange]);

  //**** Set chart options ****//
  useEffect(() => {
    // Defined separately to avoid triggering useEffect unnecessarily when used in its dependency array
    const chart = highChartsRef.current?.chart;

    while (chart?.series.length) {
      chart.series[0]?.remove();
    }

    const locale = getLocaleFromLanguage(i18n.language);

    // Set locale both globally and on the chart instance to ensure it's applied
    // This is critical because existing chart instances may not pick up global setOptions changes
    Highcharts.setOptions({
      lang: {
        locale: locale,
      },
    });

    // Update the chart instance's lang property directly to ensure locale is applied
    updateChartLocale(chart, locale);

    chart?.yAxis[0]?.update({
      ...defaultYAxisOptions,
      id: providerA,
      title: {
        ...defaultYAxisOptions.title,
        text: getAxisLabel(sensorIdA, providerA),
      },
    });

    chart?.xAxis[0]?.update({
      ...defaultXAxisOptions,
      id: providerB,
      type: 'linear',
      title: {
        ...defaultXAxisOptions.title,
        text: getAxisLabel(sensorIdB, providerB),
        margin: 20,
      },
      gridLineWidth: 0.5,
      gridLineColor: textColor,
      lineWidth: 0,
    });

    chart?.addSeries({
      showInLegend: false,
      type: 'scatter',
      data: data?.datapoints?.map(m => ({
        x: m.valueB,
        y: m.valueA,
        name: m.timestamp.toString(),
      })),
      color: scatterPlotColor,
      marker: {
        radius: scatterPlotMarkerRadius,
      },
      tooltip: {
        ...defaultTooltipOptions,
        headerFormat: '',
        pointFormatter: function () {
          const locale = getLocaleFromLanguage(i18n.language);
          const dateFormatter = new Intl.DateTimeFormat(locale, defaultLuxonChartDateTimeFormat);
          const dateTime = dateFormatter.format(new Date(this.name));
          return `${dateTime}<br>x: <strong>${roundToTwoDecimals(this.x)}</strong> ${xUnit ?? ''}<br/>y: <strong>${this.y !== undefined ? roundToTwoDecimals(this.y) : ''}</strong>  ${yUnit ?? ''}`;
        },
      },
    });
  }, [
    providerA,
    providerB,
    data?.datapoints,
    getAxisLabel,
    sensorIdA,
    sensorIdB,
    xUnit,
    yUnit,
    i18n,
    i18n.language,
  ]);

  //**** Rendering ****//
  const chartOptionsWithLocale = useChartOptionsWithLocale(options);

  return (
    <AutoResizeHighChart
      highcharts={Highcharts}
      options={chartOptionsWithLocale}
      highChartsRef={highChartsRef}
    />
  );
};
